import Card from "@mui/material/Card";
import Box from "@mui/material/Box";
import CardContent from "@mui/material/CardContent";
import CardHeader from "@mui/material/CardHeader";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useEffect, useState, useMemo, useCallback } from "react";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControl from "@mui/material/FormControl";
import { Seo } from "src/components/seo";
import { LoadingButton } from "@mui/lab";
import LinearProgress from "@mui/material/LinearProgress";
import SvgIcon from "@mui/material/SvgIcon";
import CheckCircleSolidIcon from "src/icons/untitled-ui/duocolor/check-circle-solid";
import PlacesAutocomplete from "react-places-autocomplete";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import EstatePlanLoader from "./plan-loader";
import { SeverityPill } from "src/components/severity-pill";
import useHousehold from "src/contexts/household-context";
import { useRouter } from "src/hooks/use-router";
import { paths } from "src/paths";
import { useLocation } from "react-router-dom";
import { parseStateAndPostalCode } from "src/utils/snug";
import Button from "@mui/material/Button";
import { usePeopleRolesStore } from "src/hooks/snug/use-peopleRoles";
import type { SpouseRelationshipTypes, UserDataObj } from "src/types/snugtotal";

const stateSummaries: Record<string, string> = {
  AL: `Alabama is a common law property state. Probate is typically 6 to 12 months if over $25,000 in assets. There are no state estate or inheritance taxes.`,
  AK: `Alaska is a opt-in community property state. Probate is typically 6 to 12 months if over $100k vehicles and $50k other property in assets. There are no state estate or inheritance taxes.`,
  AZ: `Arizona is a community property state. Probate is typically 9 to 18 months if over $75k personal and $100k real estate in assets. There are no state estate or inheritance taxes.`,
  AR: `Arkansas is a common law property state. Probate is typically 6 to 12 months if over $100,000 in assets. There are no state estate or inheritance taxes.`,
  CA: `California is a community property state. Probate is typically 12 to 24 months if over $184,500 in assets. There are no state estate or inheritance taxes.`,
  CO: `Colorado is a common law property state. Probate is typically 6 to 12 months if over $82,000 in assets. There are no state estate or inheritance taxes.`,
  CT: `Connecticut is a common law property state. Probate is typically 9 to 18 months if over $40,000 in assets. There are state estate taxes of 12% over $13.99M.`,
  DE: `Delaware is a common law property state. Probate is typically 9 to 12 months if over $30,000 in assets. There are no state estate or inheritance taxes.`,
  DC: `District of Columbia is a common law property state. Probate is typically 9 to 18 months if over $40,000 in assets. There are state estate taxes of up to 16% over $4.873M.`,
  FL: `Florida is a common law property state. Probate is typically 9 to 18 months if over $75,000 in assets. There are no state estate or inheritance taxes.`,
  GA: `Georgia is a common law property state. Probate is typically 6 to 12 months if over $10,000 in assets. There are no state estate or inheritance taxes.`,
  HI: `Hawaii is a common law property state. Probate is typically 9 to 18 months if over $100,000 (vehicles excluded) in assets. There are state estate taxes of up to 20% over $5.49 M.`,
  ID: `Idaho is a community property state. Probate is typically 6 to 12 months if over $100,000 in assets. There are no state estate or inheritance taxes.`,
  IL: `Illinois is a common law property state. Probate is typically 9 to 18 months if over $100,000 in assets. There are state estate taxes of up to 16% over $4 M.`,
  IN: `Indiana is a common law property state. Probate is typically 6 to 12 months if over $50,000 in assets. There are no state estate or inheritance taxes.`,
  IA: `Iowa is a common law property state. Probate is typically 9 to 12 months if over $200k in assets. There are no state estate or inheritance taxes.`,
  KS: `Kansas is a common law property state. Probate is typically 6 to 12 months if over $75,000 in assets. There are no state estate or inheritance taxes.`,
  KY: `Kentucky is a common law property state. Probate is typically 6 to 12 months if over $15,000 in assets. There are state inheritance taxes of up to 16% over $500, depending on heir class.`,
  LA: `Louisiana is a community property state. Probate is typically 9 to 18 months if over $125,000 in assets. There are no state estate or inheritance taxes.`,
  ME: `Maine is a common law property state. Probate is typically 6 to 12 months if over $40,000 in assets. There are state estate taxes of up to 12% over $7M.`,
  MD: `Maryland is a common law property state. Probate is typically 9 to 18 months if over $50k in assets ($100k if all to spouse). There are state estate taxes of up to 16% over $5M and inheritance taxes of 10% on non-exempt heirs over $1k.`,
  MA: `Massachusetts is a common law property state. Probate is typically 9 to 12 months if over $25,000 in assets and vehicle. There are state estate taxes of up to 16% over $2M.`,
  MI: `Michigan is a common law property state. Probate is typically 6 to 12 months if over $25,000 in assets and allowances. There are no state estate or inheritance taxes.`,
  MN: `Minnesota is a common law property state. Probate is typically 6 to 12 months if over $75,000 in assets. There are state estate taxes of up to 16% over $3M.`,
  MS: `Mississippi is a common law property state. Probate is typically 9 to 12 months if over $75,000 in assets. There are no state estate or inheritance taxes.`,
  MO: `Missouri is a common law property state. Probate is typically 6 to 12 months if over $40,000 in assets. There are no state estate or inheritance taxes.`,
  MT: `Montana is a common law property state. Probate is typically 6 to 12 months if over $50,000 in assets. There are no state estate or inheritance taxes.`,
  NE: `Nebraska is a common law property state. Probate is typically 6 to 12 months if over $100k in assets. There are state inheritance taxes of up to 15% over $25k, depending on heir class.`,
  NV: `Nevada is a community property state. Probate is typically 9 to 12 months if over $25k in assets ($100k if all to spouse). There are no state estate or inheritance taxes.`,
  NH: `New Hampshire is a common law property state. Probate is typically 9 to 12 months if over $10,000 in assets. There are no state estate or inheritance taxes.`,
  NJ: `New Jersey is a common law property state. Probate is typically 9 to 18 months if over $20k in assets ($50k if all to spouse). There are state inheritance taxes of up to 16% over $25k, depending on heir class.`,
  NM: `New Mexico is a community property state. Probate is typically 6 to 12 months if over $50,000 in assets. There are no state estate or inheritance taxes.`,
  NY: `New York is a common law property state. Probate is typically 9 to 24 months if over $50,000 in assets. There are state estate taxes of up to 16% over $7.16M.`,
  NC: `North Carolina is a common law property state. Probate is typically 6 to 12 months if over $20k in assets ($30k if all to spouse). There are no state estate or inheritance taxes.`,
  ND: `North Dakota is a common law property state. Probate is typically 6 to 12 months if over $50,000 in assets. There are no state estate or inheritance taxes.`,
  OH: `Ohio is a common law property state. Probate is typically 9 to 12 months if over $35k in assets ($100k if all to spouse). There are no state estate or inheritance taxes.`,
  OK: `Oklahoma is a common law property state. Probate is typically 6 to 12 months if over $200k in assets. There are no state estate or inheritance taxes.`,
  OR: `Oregon is a common law property state. Probate is typically 9 to 18 months if over $75k in personal assets and $200k in real estate. There are state estate taxes of up to 16% over $1M.`,
  PA: `Pennsylvania is a common law property state. Probate is typically 9 to 18 months if over $50,000 in assets. There are state inheritance taxes of up to 15%, depending on heir class.`,
  RI: `Rhode Island is a common law property state. Probate is typically 9 to 12 months if over $15,000 in assets. There are state estate taxes of up to 16% over $1.802M.`,
  SC: `South Carolina is a common law property state. Probate is typically 9 to 12 months if over $25,000 in assets. There are no state estate or inheritance taxes.`,
  SD: `South Dakota is a common law property state. Probate is typically 6 to 12 months if over $50,000 in assets. There are no state estate or inheritance taxes.`,
  TN: `Tennessee is a common law property state. Probate is typically 6 to 12 months if over $50,000 in assets. There are no state estate or inheritance taxes.`,
  TX: `Texas is a community property state. Probate is typically 6 to 12 months if over $75,000 in assets. There are no state estate or inheritance taxes.`,
  UT: `Utah is a common law property state. Probate is typically 6 to 12 months if over $100,000 in assets. There are no state estate or inheritance taxes.`,
  VT: `Vermont is a common law property state. Probate is typically 9 to 12 months if over $45k in assets and no real estate. There are state estate taxes of up to 16% over $5M.`,
  VA: `Virginia is a common law property state. Probate is typically 6 to 12 months if over $50,000 in assets. There are no state estate or inheritance taxes.`,
  WA: `Washington is a community property state. Probate is typically 9 to 12 months if over $100,000 in assets. There are state estate taxes of up to 20% over $2.193M.`,
  WV: `West Virginia is a common law property state. Probate is typically 6 to 12 months if over $50k in personal assets and $100k in real estate. There are no state estate or inheritance taxes.`,
  WI: `Wisconsin is a community property state. Probate is typically 9 to 12 months if over $50,000 in assets. There are no state estate or inheritance taxes.`,
  WY: `Wyoming is a common law property state. Probate is typically 6 to 12 months if over $200,000 in assets. There are no state estate or inheritance taxes.`,
};

const probateThresholds: Record<string, string> = {
  AL: `Under $25,000`,
  AK: `Under $50,000`,
  AZ: `Under $175,000`,
  AR: `Under $100,000`,
  CA: `Under $184,000`,
  CO: `Under $82,000`,
  CT: `Under $40,000`,
  DE: `Under $30,000`,
  DC: `Under $40,000`,
  FL: `Under $75,000`,
  GA: `Under $10,000`,
  HI: `Under $100,000`,
  ID: `Under $100,000`,
  IL: `Under $100,000`,
  IN: `Under $50,000`,
  IA: `Under $200,000`,
  KS: `Under $75,000`,
  KY: `Under $15,000`,
  LA: `Under $125,000`,
  ME: `Under $40,000`,
  MD: `Under $50,000`,
  MA: `Under $25,000`,
  MI: `Under $25,000`,
  MN: `Under $75,000`,
  MS: `Under $75,000`,
  MO: `Under $40,000`,
  MT: `Under $50,000`,
  NE: `Under $100,000`,
  NV: `Under $25,000`,
  NH: `Under $10,000`,
  NJ: `Under $20,000`,
  NM: `Under $50,000`,
  NY: `Under $50,000`,
  NC: `Under $20,000`,
  ND: `Under $50,000`,
  OH: `Under $35,000`,
  OK: `Under $200,000`,
  OR: `Under $275,000`,
  PA: `Under $50,000`,
  RI: `Under $15,000`,
  SC: `Under $25,000`,
  SD: `Under $50,000`,
  TN: `Under $50,000`,
  TX: `Under $75,000`,
  UT: `Under $100,000`,
  VT: `Under $45,000`,
  VA: `Under $50,000`,
  WA: `Under $100,000`,
  WV: `Under $150,000`,
  WI: `Under $50,000`,
  WY: `Under $200,000`,
};

const probateThresholdSeconds: Record<string, string> = {
  AL: `$25k - $1M`,
  AK: `$50k - $1M`,
  AZ: `$175k - $1M`,
  AR: `$100k - $1M`,
  CA: `$184k - $1M`,
  CO: `$82k - $1M`,
  CT: `$40k - $1M`,
  DE: `$30k - $1M`,
  DC: `$40k - $1M`,
  FL: `$75k - $1M`,
  GA: `$10k - $1M`,
  HI: `$100k - $1M`,
  ID: `$100k - $1M`,
  IL: `$100k - $1M`,
  IN: `$50k - $1M`,
  IA: `$200k - $1M`,
  KS: `$75k - $1M`,
  KY: `$15k - $1M`,
  LA: `$125k - $1M`,
  ME: `$40k - $1M`,
  MD: `$50k - $1M`,
  MA: `$25k - $1M`,
  MI: `$25k - $1M`,
  MN: `$75k - $1M`,
  MS: `$75k - $1M`,
  MO: `$40k - $1M`,
  MT: `$50k - $1M`,
  NE: `$100k - $1M`,
  NV: `$25k - $1M`,
  NH: `$10k - $1M`,
  NJ: `$20k - $1M`,
  NM: `$50k - $1M`,
  NY: `$50k - $1M`,
  NC: `$20k - $1M`,
  ND: `$50k - $1M`,
  OH: `$35k - $1M`,
  OK: `$200k - $1M`,
  OR: `$275k - $1M`,
  PA: `$50k - $1M`,
  RI: `$15k - $1M`,
  SC: `$25k - $1M`,
  SD: `$50k - $1M`,
  TN: `$50k - $1M`,
  TX: `$75k - $1M`,
  UT: `$100k - $1M`,
  VT: `$45k - $1M`,
  VA: `$50k - $1M`,
  WA: `$100k - $1M`,
  WV: `$150k - $1M`,
  WI: `$50k - $1M`,
  WY: `$200k - $1M`,
};

interface PersonalPlanOption {
  description: string;
  title: string;
  plural: string;
  value: string;
}

const personalPlanOptions: PersonalPlanOption[] = [
  {
    description:
      "A Joint Revocable Living Trust will cover both of you with a single, comprehensive Trust that can keep you out of probate.",
    title: "Joint Trust",
    plural: "1x",
    value: "joint",
  },
  {
    description:
      "Revocable Living Trusts are a good choice when you want to avoid probate and have individual control over beneficiaries, trustees, etc.",
    title: "Individual Trust",
    plural: "2x",
    value: "trust",
  },
  {
    description:
      "Last Will & Testaments are quick and easy to put in place, but are subject to probate and are part of the public record.",
    title: "Individual Will",
    plural: "2x",
    value: "will",
  },
  {
    description: "Upload an existing Will or Trust for secure storage and easy sharing with family, friends, and professionals.",
    title: "Upload Attorney Documents",
    plural: "",
    value: "attorney",
  },
  {
    description:
      "You'll be able to get started on your estate planning, including Powers of Attorney and Health Care Directives and make this decision later.",
    title: "Skip for now",
    plural: "",
    value: "skip",
  },
];

const Onboarding = () => {
  const {
    selectedHousehold,
    headOfHouseholDataSaving,
    onboardingHandleEditHoHUserData,
  } = useHousehold();
  const {
    peopleRoles,
    peopleRolesSavingNew,
    peopleRolesLoading,
    targetUserDatas,
    handleAddOrUpdatePeopleRolesAndUserData,
  } = usePeopleRolesStore(selectedHousehold?.head_of_household?.ud_id || null);
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const returnPath = queryParams.get("returnPath");
  const spouse_role = peopleRoles?.find((role: any) => role.role === "SPOUSE");
  const [targetUserData, setTargetUserData] = useState<UserDataObj | null>(
    null
  );
  const router = useRouter();
  useEffect(() => {
    setTargetUserData(
      targetUserDatas?.find(
        (d) => d.ud_id === spouse_role?.role_target_user_data_ud_id
      ) || null
    );
  }, [spouse_role, targetUserDatas]);
  const headOfHousehold = selectedHousehold?.head_of_household;
  const [personalPlan, setPersonalPlan] = useState<string | null>(
    headOfHousehold?.estate_plan_foundation || null
  );

  const [mode, setMode] = useState<"onboarding" | "edit">("onboarding");

  // Determine the appropriate starting step based on completed fields
  const getStartingStep = useMemo(() => {
    if (!headOfHousehold) return "marital_status";

    if (mode === "edit") {
      return "marital_status"; // Force back to this step when editing
    }

    // Detect marital status
    const hasMaritalStatus =
      spouse_role?.spouse_relationship ||
      (!spouse_role &&
        (headOfHousehold.children ||
          headOfHousehold.value_of_assets ||
          headOfHousehold.household_state_code ||
          headOfHousehold.estate_plan_foundation)
        ? "single"
        : undefined);

    const hasChildren =
      headOfHousehold.children !== null &&
      headOfHousehold.children !== undefined;
    const hasBlendedFamily =
      headOfHousehold.children === "none" ||
      (headOfHousehold.blended_family !== null &&
        headOfHousehold.blended_family !== undefined);
    const hasResidence = Boolean(
      headOfHousehold.address_text || headOfHousehold.household_state_code
    );
    const hasAssetValue = Boolean(headOfHousehold.value_of_assets);
    const hasGoals = Array.isArray(headOfHousehold.onboarding_goals);
    const hasSituations = Array.isArray(headOfHousehold.special_situations);
    const allPrerequisitesComplete =
      hasMaritalStatus &&
      hasChildren &&
      hasBlendedFamily &&
      hasResidence &&
      hasAssetValue &&
      hasGoals &&
      hasSituations;

    if (allPrerequisitesComplete) {
      return "personal_plan";
    }

    return "marital_status";
  }, [headOfHousehold, spouse_role, mode]);

  const [onboardingStep, setOnboardingStep] = useState<string>(() => {
    return getStartingStep;
  });

  useEffect(() => {
    if (onboardingStep === 'loader') {
      const timer = setTimeout(() => {
        // move to step personal plan after 6 seconds
        setOnboardingStep("personal_plan");
      }, 6000);
      return () => clearTimeout(timer); // cleanup
    }
  }, [onboardingStep, router, returnPath, personalPlan]);

  // Update onboarding step when data changes
  useEffect(() => {
    const newStep = getStartingStep;
    if (newStep !== onboardingStep && onboardingStep === "marital_status") {
      setOnboardingStep(newStep);
    }
  }, [getStartingStep, onboardingStep]);

  const [progress, setProgress] = useState<number>(10);
  useEffect(() => {
    if (onboardingStep === "marital_status") {
      setProgress(10);
    }
    if (onboardingStep === "spouse_name") {
      setProgress(15);
    }
    if (onboardingStep === "children_status") {
      setProgress(25);
    }
    if (onboardingStep === "blended_family") {
      setProgress(35);
    }
    if (onboardingStep === "residence") {
      setProgress(45);
    }
    if (onboardingStep === "asset_value") {
      setProgress(55);
    }
    if (onboardingStep === "goals") {
      setProgress(65);
    }
    if (onboardingStep === "situations") {
      setProgress(75);
    }
    if (onboardingStep === "personal_plan") {
      setProgress(85);
    }
  }, [onboardingStep]);

  const [stateOfResidence, setStateOfResidence] = useState<string>(
    selectedHousehold?.head_of_household?.household_state_code || ""
  );
  const [summary, setSummary] = useState<string>(
    () => stateSummaries[stateOfResidence as keyof typeof stateSummaries] || ""
  );
  useEffect(() => {
    setSummary(
      stateSummaries[stateOfResidence as keyof typeof stateSummaries] ?? ""
    );
  }, [stateOfResidence]);

  const [probateThreshold, setProbateThreshold] = useState<string>(
    () =>
      probateThresholds[stateOfResidence as keyof typeof probateThresholds] ||
      ""
  );
  useEffect(() => {
    setProbateThreshold(
      probateThresholds[stateOfResidence as keyof typeof probateThresholds] ??
      ""
    );
  }, [stateOfResidence]);

  const [probateThresholdSecond, setProbateThresholdSecond] = useState<string>(
    () =>
      probateThresholdSeconds[
      stateOfResidence as keyof typeof probateThresholdSeconds
      ] || ""
  );
  useEffect(() => {
    setProbateThresholdSecond(
      probateThresholdSeconds[
      stateOfResidence as keyof typeof probateThresholdSeconds
      ] ?? ""
    );
  }, [stateOfResidence]);

  const [maritalStatus, setMaritalStatus] = useState<string>("");
  const [spouseName, setSpouseName] = useState<string>("");
  const [spouseEmail, setSpouseEmail] = useState<string>("");
  const [spouseNameError, setSpouseNameError] = useState<string>("");
  const [spouseEmailError, setSpouseEmailError] = useState<string>("");

  // Populate spouse fields from existing spouse role data
  useEffect(() => {
    if (spouse_role) {
      setMaritalStatus(spouse_role.spouse_relationship || "");
      if (targetUserData) {
        setSpouseName(targetUserData.full_name || "");
        setSpouseEmail(targetUserData.contact_email || "");
      }
    } else if (
      headOfHousehold &&
      (headOfHousehold.children ||
        headOfHousehold.value_of_assets ||
        headOfHousehold.household_state_code ||
        headOfHousehold.estate_plan_foundation)
    ) {
      // If no spouse role but other fields are filled, assume single
      setMaritalStatus("single");
      setSpouseName("");
      setSpouseEmail("");
    } else {
      // Clear all fields if no data
      setMaritalStatus("");
      setSpouseName("");
      setSpouseEmail("");
    }
  }, [spouse_role, targetUserData, headOfHousehold]);

  const handleChangeMaritalStatus = (e: any) => {
    setMaritalStatus(e.target.value);
  };

  const [childrenStatus, setChildrenStatus] = useState<string>(
    headOfHousehold?.children || ""
  );
  useEffect(() => {
    setChildrenStatus(headOfHousehold?.children || "");
  }, [headOfHousehold?.children]);
  const handleChangeChildrenStatus = (e: any) => {
    setChildrenStatus(e.target.value);
  };

  const [blendedFamily, setBlendedFamily] = useState<string>(
    headOfHousehold?.blended_family === true
      ? "blended"
      : headOfHousehold?.blended_family === false
        ? "not"
        : ""
  );
  useEffect(() => {
    setBlendedFamily(
      headOfHousehold?.blended_family === true
        ? "blended"
        : headOfHousehold?.blended_family === false
          ? "not"
          : ""
    );
  }, [headOfHousehold?.blended_family]);
  const handleChangeBlendedFamily = (value: string) => {
    setBlendedFamily(value);
  };

  // Clear blended family when children is set to "none"
  useEffect(() => {
    if (
      headOfHousehold?.children === "none" &&
      headOfHousehold?.blended_family !== null &&
      headOfHousehold?.ud_id
    ) {
      onboardingHandleEditHoHUserData(headOfHousehold.ud_id, {
        blended_family: null,
      });
    }
  }, [
    headOfHousehold?.children,
    headOfHousehold?.blended_family,
    headOfHousehold?.ud_id,
    onboardingHandleEditHoHUserData,
  ]);

  const [assetValue, setAssetValue] = useState<string>(
    headOfHousehold?.value_of_assets || ""
  );
  useEffect(() => {
    setAssetValue(headOfHousehold?.value_of_assets || "");
  }, [headOfHousehold?.value_of_assets]);
  const handleChangeAssetValue = (e: any) => {
    setAssetValue(e.target.value);
  };
  useEffect(() => {
    setPersonalPlan(headOfHousehold?.estate_plan_foundation || null);
  }, [headOfHousehold?.estate_plan_foundation]);

  const [recommendedPlan, setRecommendedPlan] = useState<string | null>(null);
  const [recommendedPlanExplainer, setRecommendedPlanExplainer] = useState<
    string | null
  >(null);

  const [checkedGoals, setCheckedGoals] = useState({
    avoidProbate: false,
    privacy: false,
    minimizeTaxes: false,
    easySetup: false,
    guardianship: false,
    incapacity: false,
  });

  // Populate goals from database
  useEffect(() => {
    if (headOfHousehold?.onboarding_goals) {
      const goals = headOfHousehold.onboarding_goals;
      setCheckedGoals({
        avoidProbate: goals.includes("avoidProbate"),
        privacy: goals.includes("privacy"),
        minimizeTaxes: goals.includes("minimizeTaxes"),
        easySetup: goals.includes("easySetup"),
        guardianship: goals.includes("guardianship"),
        incapacity: goals.includes("incapacity"),
      });
    }
  }, [headOfHousehold?.onboarding_goals]);

  const handleGoalChange =
    (key: keyof typeof checkedGoals) =>
      (event: React.ChangeEvent<HTMLInputElement>) => {
        setCheckedGoals((prev) => ({
          ...prev,
          [key]: event.target.checked,
        }));
      };

  const [checkedSituations, setCheckedSituations] = useState({
    nonUs: false,
    spouseNonUs: false,
    charity: false,
    divorce: false,
    international: false,
    creditors: false,
    fight: false,
    none: false,
  });

  // Populate situations from database
  useEffect(() => {
    if (!headOfHousehold?.special_situations) return;

    const situations = headOfHousehold.special_situations;

    // 1️⃣  Work out every specific flag
    const status = {
      nonUs: situations.includes("nonUs"),
      spouseNonUs: situations.includes("spouseNonUs"),
      charity: situations.includes("charity"),
      divorce: situations.includes("divorce"),
      international: situations.includes("international"),
      creditors: situations.includes("creditors"),
      fight: situations.includes("fight"),
      none: situations.includes("none"),
    };

    // 2️⃣  Set situations and mark as interacted if data exists
    setCheckedSituations({
      ...status,
    });
  }, [headOfHousehold?.special_situations]);

  const handleSituationChange =
    (key: keyof typeof checkedSituations) =>
      (event: React.ChangeEvent<HTMLInputElement>) => {
        setCheckedSituations((prev) => {
          const newState = { ...prev };

          if (key === "none") {
            // If selecting "none", uncheck all other options
            if (event.target.checked) {
              Object.keys(newState).forEach((k) => {
                if (k !== "none")
                  newState[k as keyof typeof checkedSituations] = false;
              });
            }
            newState.none = event.target.checked;
          } else {
            // If selecting any other option, uncheck "none"
            if (event.target.checked) {
              newState.none = false;
            }
            newState[key] = event.target.checked;
          }

          return newState;
        });
      };

  // props or data you already have
  const allowWills = selectedHousehold?.professional_group?.allow_wills; // global
  const allowTrusts =
    selectedHousehold?.professional_group?.allow_trusts; // global
  const blockWill = selectedHousehold?.household_role?.block_will; // per-client
  const blockTrust = selectedHousehold?.household_role?.block_trust; // per-client

  // ✨ handy one-liners ----------------------------------------------------------
  const canWill = allowWills && !blockWill; // true → this client may create a Will
  const canTrust = allowTrusts && !blockTrust; // true → this client may create a Trust

  const isAttorney = selectedHousehold?.professional_group?.professional_classification?.includes('ATTORNEY')

  // ⛳ only keep the plans the client is allowed to choose
  const personalPlanOptionsAllowed = useMemo(
    () =>
      personalPlanOptions.filter((opt) => {
        if (opt.value === "will") return canWill;
        if (opt.value === "trust" || opt.value === "joint") return canTrust;
        if (opt.value === 'attorney') return !isAttorney;
        return true; // 'attorney' and 'skip' are always OK
      }),
    [canWill, canTrust, isAttorney]
  );

  const isPlanAllowed = useCallback(
    (plan: string) => {
      if (plan === "will") return canWill;
      if (plan === "trust" || plan === "joint") return canTrust;
      return true; // "attorney" and "skip" always allowed
    },
    [canWill, canTrust]
  );

  // ---------------------------------------------------------------
  // 🏷️  Pricing – set these once or pull from an API / env var
  // ---------------------------------------------------------------
  const trustPrice = 
    (selectedHousehold?.household_role?.trust_price || 0) / 100; // price for ONE Revocable Living Trust package
  const willPrice = (selectedHousehold?.household_role?.will_price || 0) / 100; // price for ONE Last Will & Testament package

  // utils/money.ts
  const formatUSD = (amount: number): string => {
    const wholeNumber = amount % 1 === 0;

    const formatter = new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: wholeNumber ? 0 : 2,
      maximumFractionDigits: wholeNumber ? 0 : 2,
    });

    return formatter.format(amount);
  };

  // Utility: how much does this option cost in total?
  const optionCost = (value: string): number => {
    switch (value) {
      case "joint": // one trust for both
        return trustPrice;
      case "trust": // two individual trusts
        if (maritalStatus === 'single') {
          return trustPrice;
        } else {
          return trustPrice * 2;
        }
      case "will": // two individual wills
        if (maritalStatus === 'single') {
          return willPrice;
        } else {
          return willPrice * 2;
        }
      default: // attorney / skip
        return 0;
    }
  };

  useEffect(() => {
    const reasons: string[] = [];

    /* -------- build up the 'reasons' array exactly as before -------- */
    if (checkedSituations.nonUs)
      reasons.push("being a non-US citizen has special tax considerations");
    if (checkedSituations.spouseNonUs)
      reasons.push(
        "your spouse being a non-US citizen has special tax considerations"
      );
    if (checkedSituations.charity)
      reasons.push("you want to make large charitable donations");
    if (checkedSituations.creditors)
      reasons.push("you are concerned about lawsuits or creditors");
    if (checkedSituations.divorce)
      reasons.push(
        "going through a divorce has special planning considerations"
      );
    if (checkedSituations.fight)
      reasons.push("you expect your estate might be contested");
    if (checkedSituations.international)
      reasons.push("you own significant property outside the United States");
    if (assetValue === "over_ten")
      reasons.push("you are high net worth and want to minimize estate taxes");

    const humanReadableReasons = () => {
      if (reasons.length === 0) return "";
      if (reasons.length === 1) return reasons[0];
      const last = reasons.pop();
      return `${reasons.join(", ")}, and ${last}`;
    };

    /* -------- 1️⃣ pick a *candidate* plan just like before -------- */
    let plan: string;
    let explainer: string;

    if (reasons.length > 0 || assetValue === "over_ten") {
      plan = "attorney";
      explainer = `Many people in your situation work with an attorney on their Trust or Will because ${humanReadableReasons()}. Creating an account will allow you to create any additional documents you need, including Powers of Attorney and Health Care Directives.`;
    } else if (assetValue === "exempt" && !checkedGoals.avoidProbate) {
      plan = "will";
      explainer =
        "Many people in your situation use a Last Will & Testament as a quick, easy, and solid estate plan.";
    } else if (
      checkedGoals.easySetup &&
      !checkedGoals.avoidProbate &&
      !checkedGoals.privacy &&
      !(assetValue === "up_to_ten" || assetValue === "up_to_five")
    ) {
      plan = "will";
      explainer =
        "Many people in your situation use a Last Will & Testament as a quick, easy, and solid foundation.";
    } else {
      if (maritalStatus === "single") {
        plan = "trust";
        explainer =
          "Many people in your situation use a Revocable Living Trust as the foundation of their estate plan.";
      } else if (blendedFamily === "blended") {
        plan = "trust";
        explainer =
          "Many people with blended families use separate Individual Revocable Living Trusts to specify different sets of beneficiaries.";
      } else {
        plan = "joint";
        explainer =
          "Many people in your situation use a Joint Revocable Living Trust as the foundation of their estate plan.";
      }
    }

    /* -------- 2️⃣ if that candidate isn't allowed, fall back -------- */
    if (!isPlanAllowed(plan)) {
      if (plan === "will" && canTrust)
        plan = maritalStatus === "single" ? "trust" : "joint";
      else if ((plan === "trust" || plan === "joint") && canWill) plan = "will";
      else plan = "attorney"; // last-ditch fallback
    }

    /* -------- 3️⃣ commit to state -------- */
    setRecommendedPlan(plan);
    setRecommendedPlanExplainer(explainer);
    // setPersonalPlan(plan);          // keep the "default selected" behaviour
  }, [
    maritalStatus,
    childrenStatus,
    blendedFamily,
    assetValue,
    checkedGoals,
    checkedSituations,
    stateOfResidence,
    canWill,
    canTrust,
    isPlanAllowed,
  ]);

  const [address, setAddress] = useState("");
  const [householdStateCode, setHouseholdStateCode] = useState("");

  const handleAddressChange = (value: string) => {
    setAddress(value);
  };

  const handleAddressSelect = async (value: string) => {
    setAddress(value);
    const { state_code } = await parseStateAndPostalCode(value);
    setHouseholdStateCode(state_code);
    setStateOfResidence(state_code);
  };

  useEffect(() => {
    const addressText = selectedHousehold?.head_of_household?.address_text || "";
    const stateCode = selectedHousehold?.head_of_household?.household_state_code || "";

    setAddress(addressText);
    setHouseholdStateCode(stateCode);

    // Also update stateOfResidence if there's an existing state code
    if (stateCode) {
      setStateOfResidence(stateCode);
    } else if (addressText && !stateCode) {
      // If address exists but no state code, extract it from the address
      parseStateAndPostalCode(addressText).then(({ state_code }) => {
        if (state_code) {
          setHouseholdStateCode(state_code);
          setStateOfResidence(state_code);
        }
      }).catch(err => {
        console.error("Error parsing state from pre-filled address:", err);
      });
    }
  }, [selectedHousehold]);

  // Add skip handler
  const handleSkipOnboarding = async () => {
    if (!headOfHousehold?.ud_id) return;
    try {
      await onboardingHandleEditHoHUserData(headOfHousehold.ud_id, {
        show_household_onboarding_requirement: false,
      });
      // Navigate back to the original path if returnPath is provided, otherwise go to dashboard
      const navigationPath = returnPath || paths.dashboard.index;
      router.push(navigationPath);
    } catch (err) {
      console.error(err);
    }
  };

  // Pre-populate spouse fields if spouse PeopleRole exists
  useEffect(() => {
    if (spouse_role && targetUserData) {
      setMaritalStatus(spouse_role.spouse_relationship || "");
      setSpouseName(targetUserData.full_name || "");
      setSpouseEmail(targetUserData.contact_email || "");
    }
  }, [spouse_role, targetUserData]);

  return (
    <>
      <Seo title="Total Estate Planning Login" />
      <div style={{ position: "relative" }}>
        <Stack direction="column" spacing={1}>
          <Card
            variant="outlined"
            sx={{
              borderRadius: 1,
              p: {
                xs: 1, // padding on extra small screens
                sm: 2, // padding on small screens
                md: 3, // padding on medium screens
                lg: 3, // padding on large screens
                xl: 3, // padding on extra large screens
              },
              px: {
                xs: 1, // padding on extra small screens
                sm: 1, // padding on small screens
                md: 4, // padding on medium screens
                lg: 4, // padding on large screens
                xl: 4, // padding on extra large screens
              },
              pb: 4,
            }}
          >
            <Box sx={{ width: "100%", px: 3 }}>
              <LinearProgress variant="determinate" value={progress} />
            </Box>
            <CardHeader
              sx={{ pb: 0 }}
              title={
                <>
                  {onboardingStep === "marital_status" && (
                    <Stack spacing={1}>
                      <Typography variant="h5">Are you married?</Typography>
                      <Typography variant="body2" color="text.secondary">
                        We'll help make sure the whole family is covered with a
                        coordinated estate plan.
                      </Typography>
                    </Stack>
                  )}
                  {onboardingStep === "spouse_name" && (
                    <Stack spacing={1}>
                      <Typography variant="h5">
                        What is your{" "}
                        {maritalStatus === "married" ? `spouse's` : `partner's`}{" "}
                        name?
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        We'll add your{" "}
                        {maritalStatus === "married" ? `spouse` : `partner`} to
                        the account and your personalized document checklist.
                      </Typography>
                    </Stack>
                  )}
                  {onboardingStep === "children_status" && (
                    <Stack spacing={1}>
                      <Typography variant="h5">
                        Do you have children?
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        A well-designed estate plan can cover children of any
                        age, whether minors or adults.
                      </Typography>
                    </Stack>
                  )}
                  {onboardingStep === "blended_family" &&
                    headOfHousehold?.children &&
                    headOfHousehold.children !== "none" && (
                      <Stack spacing={1}>
                        <Typography variant="h5">
                          Are you a blended family?
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          There are special strategies available if you or your{" "}
                          {maritalStatus === "married" ? `spouse` : `partner`}{" "}
                          have children from past relationships.
                        </Typography>
                      </Stack>
                    )}
                  {onboardingStep === "residence" && (
                    <Stack spacing={1}>
                      <Typography variant="h5">Where do you live?</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Every document will be tailored to your local state law,
                        including any estate or inheritance taxes.
                      </Typography>
                    </Stack>
                  )}
                  {onboardingStep === "asset_value" && (
                    <Stack spacing={1}>
                      <Typography variant="h5">
                        What is your net worth?
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        The value of your assets will guide the best strategy
                        and documents for your estate planning.
                      </Typography>
                    </Stack>
                  )}
                  {onboardingStep === "goals" && (
                    <Stack spacing={1}>
                      <Typography variant="h5">
                        What are your top goals?
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Your estate plan will be designed to meet your personal
                        goals, during life and after death.
                      </Typography>
                    </Stack>
                  )}
                  {onboardingStep === "situations" && (
                    <Stack spacing={1}>
                      <Typography variant="h5">
                        Do any of these situations apply?
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        These situations can lead to a more complex estate that
                        may benefit from an estate planning attorney.
                      </Typography>
                    </Stack>
                  )}
                  {onboardingStep === "personal_plan" && (
                    <Stack spacing={1}>
                      <Typography variant="h5">Choose your plan</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Wills and Trusts are the foundation of any estate plan.
                        You can always change your mind later.
                      </Typography>
                    </Stack>
                  )}
                </>
              }
            />
            <CardContent sx={{ pt: 2 }}>
              {onboardingStep === "marital_status" && (
                <Stack spacing={3}>
                  <FormControl>
                    <RadioGroup
                      aria-labelledby="demo-radio-buttons-group-label"
                      name="marital_status"
                      value={maritalStatus}
                      onChange={handleChangeMaritalStatus}
                    >
                      <FormControlLabel
                        value="single"
                        control={<Radio size="small" />}
                        label={
                          <Typography color="text.secondary" variant="body2">
                            Single
                          </Typography>
                        }
                      />
                      <FormControlLabel
                        value="married"
                        control={<Radio size="small" />}
                        label={
                          <Typography color="text.secondary" variant="body2">
                            Married
                          </Typography>
                        }
                      />
                      <FormControlLabel
                        value="domestic_partnership"
                        control={<Radio size="small" />}
                        label={
                          <Typography color="text.secondary" variant="body2">
                            Domestic Partnership
                          </Typography>
                        }
                      />
                      <FormControlLabel
                        value="civil_union"
                        control={<Radio size="small" />}
                        label={
                          <Typography color="text.secondary" variant="body2">
                            Civil Union
                          </Typography>
                        }
                      />
                    </RadioGroup>
                  </FormControl>
                  {maritalStatus && (
                    <Card
                      variant="outlined"
                      sx={{
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        backgroundColor: "#FAF9F8",
                      }}
                    >
                      <CardContent
                        sx={{
                          flex: 1,
                          display: "flex",
                          flexDirection: "column",
                          position: "relative",
                          p: 2,
                          "&:last-child": {
                            paddingBottom: 3,
                          },
                        }}
                      >
                        <Stack
                          direction="row"
                          spacing={2}
                          alignItems={"center"}
                        >
                          <SvgIcon fontSize="large" sx={{ color: "#3e8a28" }}>
                            <CheckCircleSolidIcon />
                          </SvgIcon>
                          <Stack direction="column" spacing={1}>
                            <Typography sx={{ mt: 1, flex: 1 }} variant="body2">
                              Most{" "}
                              {maritalStatus === "single"
                                ? `people`
                                : `couples`}{" "}
                              can complete all of their estate planning
                              documents in under{" "}
                              {maritalStatus === "single" ? `20` : `30`}{" "}
                              minutes. We'll guide you each step of the way.
                            </Typography>
                          </Stack>
                        </Stack>
                      </CardContent>
                    </Card>
                  )}
                  {maritalStatus === "single" ? (
                    <LoadingButton
                      disabled={
                        peopleRolesLoading ||
                        peopleRolesSavingNew ||
                        !maritalStatus
                      }
                      fullWidth
                      endIcon={<ArrowForwardIcon />}
                      size="large"
                      sx={{ mt: 4 }}
                      type="button"
                      variant="contained"
                      loading={peopleRolesSavingNew || peopleRolesLoading}
                      onClick={async () => {
                        if (!headOfHousehold?.ud_id) return;

                        setOnboardingStep("children_status");
                      }}
                    >
                      {`Save and continue`}
                    </LoadingButton>
                  ) : (
                    <LoadingButton
                      disabled={
                        peopleRolesLoading ||
                        peopleRolesSavingNew ||
                        !maritalStatus
                      }
                      fullWidth
                      endIcon={<ArrowForwardIcon />}
                      size="large"
                      sx={{ mt: 4 }}
                      type="button"
                      variant="contained"
                      loading={peopleRolesLoading || peopleRolesSavingNew}
                      onClick={async () => {
                        if (!headOfHousehold?.ud_id) return;
                        setOnboardingStep("spouse_name");
                      }}
                    >
                      {`Save and continue`}
                    </LoadingButton>
                  )}
                </Stack>
              )}
              {onboardingStep === "spouse_name" && (
                <Stack spacing={3}>
                  <Stack spacing={1} sx={{ width: "100%" }}>
                    <TextField
                      autoFocus
                      error={!!spouseNameError}
                      fullWidth
                      helperText={spouseNameError}
                      label={
                        [
                          "married",
                          "domestic_partnership",
                          "civil_union",
                        ].includes(maritalStatus)
                          ? `Spouse name`
                          : `Partner name`
                      }
                      name="spouse_name"
                      onChange={(e) => {
                        setSpouseName(e.target.value);
                        setSpouseNameError("");
                      }}
                      type="name"
                      value={spouseName}
                    />
                    <TextField
                      fullWidth
                      label={
                        [
                          "married",
                          "domestic_partnership",
                          "civil_union",
                        ].includes(maritalStatus)
                          ? `Spouse email`
                          : `Partner email`
                      }
                      name="spouse_email"
                      type="email"
                      value={spouseEmail}
                      onChange={(e) => {
                        setSpouseEmail(e.target.value);
                        setSpouseEmailError("");
                      }}
                      onBlur={(e) => {
                        const value = e.target.value.trim();
                        if (!value) {
                          setSpouseEmailError("Email is required");
                        } else if (!/^\S+@\S+\.\S+$/.test(value)) {
                          setSpouseEmailError(
                            "Please enter a valid email address"
                          );
                        } else if (
                          value.toLowerCase() ===
                          headOfHousehold?.contact_email?.toLowerCase()
                        ) {
                          setSpouseEmailError(
                            "Spouse email cannot be the same as your email"
                          );
                        } else {
                          setSpouseEmailError("");
                        }
                      }}
                      error={!!spouseEmailError}
                      helperText={spouseEmailError}
                    />
                  </Stack>
                  <Stack spacing={1} sx={{ mb: "-40px !important" }}>
                    <LoadingButton
                      disabled={
                        peopleRolesLoading ||
                        !!spouseEmailError ||
                        peopleRolesSavingNew ||
                        !spouseName ||
                        !spouseEmail
                      }
                      fullWidth
                      endIcon={<ArrowForwardIcon />}
                      size="large"
                      sx={{ mt: 4 }}
                      type="button"
                      variant="contained"
                      loading={peopleRolesLoading || peopleRolesSavingNew}
                      onClick={async () => {
                        // Validate spouse name
                        if (!spouseName) {
                          setSpouseNameError("Spouse name is required");
                          return;
                        }
                        // Validate spouse email
                        if (!spouseEmail) {
                          setSpouseEmailError("Email is required");
                          return;
                        }
                        if (!/^\S+@\S+\.\S+$/.test(spouseEmail)) {
                          setSpouseEmailError(
                            "Please enter a valid email address"
                          );
                          return;
                        }
                        if (
                          spouseEmail.toLowerCase() ===
                          headOfHousehold?.contact_email?.toLowerCase()
                        ) {
                          setSpouseEmailError(
                            "Spouse email cannot be the same as your email"
                          );
                          return;
                        }
                        try {
                          if (!headOfHousehold?.ud_id) return;
                          // Save or update spouse PeopleRole
                          await handleAddOrUpdatePeopleRolesAndUserData(
                            headOfHousehold.ud_id,
                            {
                              id: spouse_role?.id || null,
                              role: "SPOUSE",
                              spouse_relationship: [
                                "married",
                                "domestic_partnership",
                                "civil_union",
                              ].includes(maritalStatus)
                                ? (maritalStatus as SpouseRelationshipTypes)
                                : undefined,
                              is_household_member: true,
                              role_target_user_data: {
                                full_name: spouseName,
                                contact_email: spouseEmail,
                              },
                            }
                          );
                          setOnboardingStep("children_status");
                        } catch (err) {
                          console.error(err);
                        }
                      }}
                    >
                      {`Save and continue`}
                    </LoadingButton>
                    <Button
                      variant="text"
                      color="inherit"
                      onClick={() => {
                        setOnboardingStep("marital_status");
                        setMode("edit");
                      }}
                    >
                      Back
                    </Button>
                  </Stack>
                </Stack>
              )}
              {onboardingStep === "children_status" && (
                <Stack spacing={3}>
                  <FormControl>
                    <RadioGroup
                      aria-labelledby="demo-radio-buttons-group-label"
                      name="children_status"
                      value={childrenStatus}
                      onChange={handleChangeChildrenStatus}
                    >
                      <FormControlLabel
                        value="none"
                        control={<Radio size="small" />}
                        label={
                          <Typography color="text.secondary" variant="body2">
                            No children
                          </Typography>
                        }
                      />
                      <FormControlLabel
                        value="adult"
                        control={<Radio size="small" />}
                        label={
                          <Typography color="text.secondary" variant="body2">
                            All children over 18
                          </Typography>
                        }
                      />
                      <FormControlLabel
                        value="minor"
                        control={<Radio size="small" />}
                        label={
                          <Typography color="text.secondary" variant="body2">
                            At least one child under 18
                          </Typography>
                        }
                      />
                    </RadioGroup>
                  </FormControl>
                  {childrenStatus && (
                    <Card
                      variant="outlined"
                      sx={{
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        backgroundColor: "#FAF9F8",
                      }}
                    >
                      <CardContent
                        sx={{
                          flex: 1,
                          display: "flex",
                          flexDirection: "column",
                          position: "relative",
                          p: 2,
                          "&:last-child": {
                            paddingBottom: 3,
                          },
                        }}
                      >
                        <Stack
                          direction="row"
                          spacing={2}
                          alignItems={"center"}
                        >
                          <SvgIcon fontSize="large" sx={{ color: "#3e8a28" }}>
                            <CheckCircleSolidIcon />
                          </SvgIcon>
                          <Stack direction="column" spacing={1}>
                            {childrenStatus === "none" && (
                              <Typography
                                sx={{ mt: 1, flex: 1 }}
                                variant="body2"
                              >
                                It is easy to create a custom list of
                                beneficiaries, including family, friends, and
                                charitable organizations.
                              </Typography>
                            )}
                            {childrenStatus === "adult" && (
                              <Typography
                                sx={{ mt: 1, flex: 1 }}
                                variant="body2"
                              >
                                You will be able to assign adult children as
                                beneficiaries, executors, trustees, power of
                                attorney agents, and more.
                              </Typography>
                            )}
                            {childrenStatus === "minor" && (
                              <Typography
                                sx={{ mt: 1, flex: 1 }}
                                variant="body2"
                              >
                                You will be able to assign backup guardians and
                                manage the inheritance for any minor children in
                                your family.
                              </Typography>
                            )}
                          </Stack>
                        </Stack>
                      </CardContent>
                    </Card>
                  )}
                  <Stack spacing={1} sx={{ mb: "-40px !important" }}>
                    <LoadingButton
                      disabled={headOfHouseholDataSaving || !childrenStatus}
                      fullWidth
                      endIcon={<ArrowForwardIcon />}
                      size="large"
                      sx={{ mt: 4 }}
                      type="button"
                      variant="contained"
                      loading={headOfHouseholDataSaving}
                      onClick={async () => {
                        try {
                          if (!headOfHousehold?.ud_id) return;
                          await onboardingHandleEditHoHUserData(
                            headOfHousehold.ud_id,
                            {
                              children: childrenStatus,
                            }
                          );
                          // Skip blended family step if no children
                          if (childrenStatus === "none") {
                            setOnboardingStep("residence");
                          } else {
                            setOnboardingStep("blended_family");
                          }
                        } catch (err) {
                          console.error(err);
                        }
                      }}
                    >
                      {`Save and continue`}
                    </LoadingButton>
                    {maritalStatus === "single" ? (
                      <Button
                        variant="text"
                        color="inherit"
                        onClick={() => setOnboardingStep("marital_status")}
                      >
                        Back
                      </Button>
                    ) : (
                      <Button
                        variant="text"
                        color="inherit"
                        onClick={() => setOnboardingStep("spouse_name")}
                      >
                        Back
                      </Button>
                    )}
                  </Stack>
                </Stack>
              )}
              {onboardingStep === "blended_family" &&
                headOfHousehold?.children &&
                headOfHousehold.children !== "none" && (
                  <Stack spacing={3}>
                    <FormControl>
                      <RadioGroup
                        aria-labelledby="demo-radio-buttons-group-label"
                        name="blended_family"
                        value={blendedFamily}
                        onChange={(e) =>
                          handleChangeBlendedFamily(e.target.value)
                        }
                      >
                        <FormControlLabel
                          value="blended"
                          control={<Radio size="small" />}
                          label={
                            <Typography color="text.secondary" variant="body2">
                              Yes, we are a blended family
                            </Typography>
                          }
                        />
                        <FormControlLabel
                          value="not"
                          control={<Radio size="small" />}
                          label={
                            <Typography color="text.secondary" variant="body2">
                              No, all children are ours together
                            </Typography>
                          }
                        />
                      </RadioGroup>
                    </FormControl>
                    {blendedFamily && (
                      <Card
                        variant="outlined"
                        sx={{
                          height: "100%",
                          display: "flex",
                          flexDirection: "column",
                          backgroundColor: "#FAF9F8",
                        }}
                      >
                        <CardContent
                          sx={{
                            flex: 1,
                            display: "flex",
                            flexDirection: "column",
                            position: "relative",
                            p: 2,
                            "&:last-child": {
                              paddingBottom: 3,
                            },
                          }}
                        >
                          <Stack
                            direction="row"
                            spacing={2}
                            alignItems={"center"}
                          >
                            <SvgIcon fontSize="large" sx={{ color: "#3e8a28" }}>
                              <CheckCircleSolidIcon />
                            </SvgIcon>
                            <Stack direction="column" spacing={1}>
                              {blendedFamily === "blended" && (
                                <Typography
                                  sx={{ mt: 1, flex: 1 }}
                                  variant="body2"
                                >
                                  Great, planning for a blended family typically
                                  uses individual Wills or Trusts to ensure each
                                  set of children is properly set up and
                                  protected as beneficiaries.
                                </Typography>
                              )}
                              {blendedFamily === "not" && (
                                <Typography
                                  sx={{ mt: 1, flex: 1 }}
                                  variant="body2"
                                >
                                  Great, you'll have a full range of options for
                                  your estate planning, including a Joint Trust
                                  or Individual Wills/Trusts for you and your{" "}
                                  {maritalStatus === "married"
                                    ? `spouse`
                                    : `partner`}
                                  .
                                </Typography>
                              )}
                            </Stack>
                          </Stack>
                        </CardContent>
                      </Card>
                    )}
                    <Stack spacing={1} sx={{ mb: "-40px !important" }}>
                      <LoadingButton
                        disabled={headOfHouseholDataSaving || !blendedFamily}
                        fullWidth
                        endIcon={<ArrowForwardIcon />}
                        size="large"
                        sx={{ mt: 4 }}
                        type="button"
                        variant="contained"
                        loading={headOfHouseholDataSaving}
                        onClick={async () => {
                          try {
                            if (!headOfHousehold?.ud_id) return;
                            await onboardingHandleEditHoHUserData(
                              headOfHousehold.ud_id,
                              {
                                blended_family:
                                  blendedFamily === "blended" ? true : false,
                              }
                            );
                            setOnboardingStep("residence");
                          } catch (err) {
                            console.error(err);
                          }
                        }}
                      >
                        {`Save and continue`}
                      </LoadingButton>
                      <Button
                        variant="text"
                        color="inherit"
                        onClick={() => setOnboardingStep("children_status")}
                      >
                        Back
                      </Button>
                    </Stack>
                  </Stack>
                )}
              {onboardingStep === "residence" && (
                <Stack spacing={3}>
                  <Stack spacing={1} sx={{ width: "100%" }}>
                    <PlacesAutocomplete
                      key="account_address"
                      value={address}
                      onChange={handleAddressChange}
                      onSelect={handleAddressSelect}
                      searchOptions={{
                        types: ["address"],
                        componentRestrictions: { country: "us" },
                      }}
                    >
                      {({
                        getInputProps,
                        suggestions,
                        getSuggestionItemProps,
                        loading,
                      }) => (
                        <div
                          style={{
                            width: "100%",
                            textAlign: "left",
                            marginBottom: 8,
                          }}
                        >
                          <div
                            style={{
                              position: "relative",
                              display: "inline-block",
                              width: "100%",
                            }}
                          >
                            <div className="address-div">
                              <TextField
                                fullWidth
                                label="Address"
                                name="addressautocomplete"
                                {...getInputProps({
                                  placeholder: "Enter your address",
                                  className:
                                    "location-search-input address-input",
                                  autoComplete: "new-user-street-address",
                                })}
                              />
                            </div>
                            <div
                              className="autocomplete-dropdown-container"
                              style={{ backgroundColor: "white" }}
                            >
                              {loading && <></>}
                              {suggestions.map((suggestion) => {
                                const className = suggestion.active
                                  ? "suggestion-item--active"
                                  : "suggestion-item";
                                const style = suggestion.active
                                  ? {
                                    backgroundColor: "#fafafa",
                                    cursor: "pointer",
                                    padding: "5px 15px",
                                    marginBottom: 6,
                                  }
                                  : {
                                    backgroundColor: "#ffffff",
                                    cursor: "pointer",
                                    padding: "10px 15px",
                                    marginBottom: 6,
                                  };
                                return (
                                  <div
                                    {...getSuggestionItemProps(suggestion, {
                                      className,
                                      style,
                                    })}
                                    key={suggestion.placeId}
                                  >
                                    <span>{suggestion.description}</span>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        </div>
                      )}
                    </PlacesAutocomplete>
                  </Stack>
                  {summary && (
                    <Card
                      variant="outlined"
                      sx={{
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        backgroundColor: "#FAF9F8",
                      }}
                    >
                      <CardContent
                        sx={{
                          flex: 1,
                          display: "flex",
                          flexDirection: "column",
                          position: "relative",
                          p: 2,
                          "&:last-child": { paddingBottom: 3 },
                        }}
                      >
                        <Stack
                          direction="row"
                          spacing={2}
                          alignItems={"center"}
                        >
                          <SvgIcon fontSize="large" sx={{ color: "#3e8a28" }}>
                            <CheckCircleSolidIcon />
                          </SvgIcon>
                          <Stack direction="column" spacing={1}>
                            <Typography sx={{ mt: 1, flex: 1 }} variant="body2">
                              <b>We have you covered!</b> {summary}
                            </Typography>
                          </Stack>
                        </Stack>
                      </CardContent>
                    </Card>
                  )}
                  <Stack spacing={1} sx={{ mb: "-40px !important" }}>
                    <LoadingButton
                      disabled={
                        headOfHouseholDataSaving ||
                        !address ||
                        !householdStateCode
                      }
                      fullWidth
                      endIcon={<ArrowForwardIcon />}
                      size="large"
                      sx={{ mt: 4 }}
                      type="button"
                      variant="contained"
                      loading={headOfHouseholDataSaving}
                      onClick={async () => {
                        try {
                          if (!headOfHousehold?.ud_id) return;
                          await onboardingHandleEditHoHUserData(
                            headOfHousehold.ud_id,
                            {
                              address_text: address,
                              household_state_code: householdStateCode,
                            }
                          );
                          setOnboardingStep("asset_value");
                        } catch (err) {
                          console.error(err);
                        }
                      }}
                    >
                      {`Save and continue`}
                    </LoadingButton>
                    {childrenStatus === "none" ? (
                      <Button
                        variant="text"
                        color="inherit"
                        onClick={() => setOnboardingStep("children_status")}
                      >
                        Back
                      </Button>
                    ) : (
                      <Button
                        variant="text"
                        color="inherit"
                        onClick={() => setOnboardingStep("blended_family")}
                      >
                        Back
                      </Button>
                    )}
                  </Stack>
                </Stack>
              )}
              {onboardingStep === "asset_value" && (
                <Stack spacing={3}>
                  <FormControl>
                    <RadioGroup
                      aria-labelledby="demo-radio-buttons-group-label"
                      name="asset_value"
                      value={assetValue}
                      onChange={handleChangeAssetValue}
                    >
                      <FormControlLabel
                        value="exempt"
                        control={<Radio size="small" />}
                        label={
                          <Typography color="text.secondary" variant="body2">
                            {probateThreshold}
                          </Typography>
                        }
                      />
                      <FormControlLabel
                        value="up_to_one"
                        control={<Radio size="small" />}
                        label={
                          <Typography color="text.secondary" variant="body2">
                            {probateThresholdSecond}
                          </Typography>
                        }
                      />
                      <FormControlLabel
                        value="up_to_five"
                        control={<Radio size="small" />}
                        label={
                          <Typography color="text.secondary" variant="body2">
                            $1M - $5M
                          </Typography>
                        }
                      />
                      <FormControlLabel
                        value="up_to_ten"
                        control={<Radio size="small" />}
                        label={
                          <Typography color="text.secondary" variant="body2">
                            $5M - $10M
                          </Typography>
                        }
                      />
                      <FormControlLabel
                        value="over_ten"
                        control={<Radio size="small" />}
                        label={
                          <Typography color="text.secondary" variant="body2">
                            Over $10M
                          </Typography>
                        }
                      />
                    </RadioGroup>
                  </FormControl>
                  {assetValue && (
                    <Card
                      variant="outlined"
                      sx={{
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        backgroundColor: "#FAF9F8",
                      }}
                    >
                      <CardContent
                        sx={{
                          flex: 1,
                          display: "flex",
                          flexDirection: "column",
                          position: "relative",
                          p: 2,
                          "&:last-child": {
                            paddingBottom: 3,
                          },
                        }}
                      >
                        <Stack
                          direction="row"
                          spacing={2}
                          alignItems={"center"}
                        >
                          <SvgIcon fontSize="large" sx={{ color: "#3e8a28" }}>
                            <CheckCircleSolidIcon />
                          </SvgIcon>
                          <Stack direction="column" spacing={1}>
                            {assetValue === "exempt" && (
                              <Typography
                                sx={{ mt: 1, flex: 1 }}
                                variant="body2"
                              >
                                Based on the value of your assets, you may
                                qualify to skip probate. This means both a Will
                                or Trust may be good options for you.
                              </Typography>
                            )}
                            {assetValue === "up_to_one" && (
                              <Typography
                                sx={{ mt: 1, flex: 1 }}
                                variant="body2"
                              >
                                Based on the value of your assets, you are
                                likely subject to probate. A Trust is a great
                                way to skip probate and ease the burden on
                                family.
                              </Typography>
                            )}
                            {assetValue === "up_to_five" && (
                              <Typography
                                sx={{ mt: 1, flex: 1 }}
                                variant="body2"
                              >
                                Based on the value of your assets, you are
                                likely subject to probate. A Trust is a great
                                way to skip probate and ease the burden on
                                family.
                              </Typography>
                            )}
                            {assetValue === "up_to_ten" && (
                              <Typography
                                sx={{ mt: 1, flex: 1 }}
                                variant="body2"
                              >
                                Based on the value of your assets, you are
                                likely subject to probate. A Trust is a great
                                way to skip probate and ease the burden on
                                family.
                              </Typography>
                            )}
                            {assetValue === "over_ten" && (
                              <Typography
                                sx={{ mt: 1, flex: 1 }}
                                variant="body2"
                              >
                                Based on the value of your assets, you may be
                                subject to federal estate taxes. You can consult
                                with an attorney to help with your tax strategy.
                              </Typography>
                            )}
                          </Stack>
                        </Stack>
                      </CardContent>
                    </Card>
                  )}
                  <Stack spacing={1} sx={{ mb: "-40px !important" }}>
                    <LoadingButton
                      disabled={headOfHouseholDataSaving || !assetValue}
                      fullWidth
                      endIcon={<ArrowForwardIcon />}
                      size="large"
                      sx={{ mt: 4 }}
                      type="button"
                      variant="contained"
                      loading={headOfHouseholDataSaving}
                      onClick={async () => {
                        try {
                          if (!headOfHousehold?.ud_id) return;
                          await onboardingHandleEditHoHUserData(
                            headOfHousehold.ud_id,
                            {
                              value_of_assets: assetValue,
                            }
                          );
                          setOnboardingStep("goals");
                        } catch (err) {
                          console.error(err);
                        }
                      }}
                    >
                      {`Save and continue`}
                    </LoadingButton>
                    <Button
                      variant="text"
                      color="inherit"
                      onClick={() => setOnboardingStep("residence")}
                    >
                      Back
                    </Button>
                  </Stack>
                </Stack>
              )}
              {onboardingStep === "goals" && (
                <Stack spacing={2} sx={{ ml: -1 }}>
                  <Box
                    sx={{
                      alignItems: "center",
                      display: "flex",
                    }}
                  >
                    <Checkbox
                      checked={checkedGoals.avoidProbate}
                      name="avoid_probate"
                      onChange={handleGoalChange("avoidProbate")}
                    />
                    <Typography color="text.secondary" variant="body2">
                      <b>Avoid Probate</b> - I want my beneficiaries to receive
                      their inheritance quickly.
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      alignItems: "center",
                      display: "flex",
                    }}
                  >
                    <Checkbox
                      checked={checkedGoals.privacy}
                      name="privacy"
                      onChange={handleGoalChange("privacy")}
                    />
                    <Typography color="text.secondary" variant="body2">
                      <b>Privacy</b> - I want my estate plans to be private and
                      kept out of the public record.
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      alignItems: "center",
                      display: "flex",
                    }}
                  >
                    <Checkbox
                      checked={checkedGoals.minimizeTaxes}
                      name="minimize_taxes"
                      onChange={handleGoalChange("minimizeTaxes")}
                    />
                    <Typography color="text.secondary" variant="body2">
                      <b>Minimize Taxes</b> - I want to minimize any federal or
                      state taxes, either on the estate or inheritance.
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      alignItems: "center",
                      display: "flex",
                    }}
                  >
                    <Checkbox
                      checked={checkedGoals.easySetup}
                      name="easy_setup"
                      onChange={handleGoalChange("easySetup")}
                    />
                    <Typography color="text.secondary" variant="body2">
                      <b>Easy Setup</b> - I want a quick and easy option, even
                      if that means more work down the road for family.
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      alignItems: "center",
                      display: "flex",
                    }}
                  >
                    <Checkbox
                      checked={checkedGoals.guardianship}
                      name="guardianship"
                      onChange={handleGoalChange("guardianship")}
                    />
                    <Typography color="text.secondary" variant="body2">
                      <b>Guardianship</b> - I want to specify backup guardians
                      for any minor children.
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      alignItems: "center",
                      display: "flex",
                    }}
                  >
                    <Checkbox
                      checked={checkedGoals.incapacity}
                      name="incapacity"
                      onChange={handleGoalChange("incapacity")}
                    />
                    <Typography color="text.secondary" variant="body2">
                      <b>Incapacity</b> - I want to choose who can make
                      financial and medical decisions if I can't.
                    </Typography>
                  </Box>
                  <Stack spacing={1} sx={{ mb: "-40px !important" }}>
                    <LoadingButton
                      disabled={headOfHouseholDataSaving}
                      fullWidth
                      endIcon={<ArrowForwardIcon />}
                      size="large"
                      sx={{ mt: 4 }}
                      type="button"
                      variant="contained"
                      loading={headOfHouseholDataSaving}
                      onClick={async () => {
                        try {
                          if (!headOfHousehold?.ud_id) return;
                          // Convert checkedGoals object to array of selected keys
                          const selectedGoals = Object.keys(
                            checkedGoals
                          ).filter(
                            (key) =>
                              checkedGoals[key as keyof typeof checkedGoals]
                          );
                          await onboardingHandleEditHoHUserData(
                            headOfHousehold.ud_id,
                            {
                              onboarding_goals: selectedGoals,
                            }
                          );
                          setOnboardingStep("situations");
                        } catch (err) {
                          console.error(err);
                        }
                      }}
                    >
                      {`Save and continue`}
                    </LoadingButton>
                    <Button
                      variant="text"
                      color="inherit"
                      onClick={() => setOnboardingStep("asset_value")}
                    >
                      Back
                    </Button>
                  </Stack>
                </Stack>
              )}
              {onboardingStep === "situations" && (
                <Stack spacing={0} sx={{ ml: -1 }}>
                  <Box
                    sx={{
                      alignItems: "center",
                      display: "flex",
                    }}
                  >
                    <Checkbox
                      checked={checkedSituations.nonUs}
                      name="non_us"
                      onChange={handleSituationChange("nonUs")}
                    />
                    <Typography color="text.secondary" variant="body2">
                      I am not a US citizen.
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      alignItems: "center",
                      display: "flex",
                    }}
                  >
                    <Checkbox
                      checked={checkedSituations.spouseNonUs}
                      name="spouse_non_us"
                      onChange={handleSituationChange("spouseNonUs")}
                    />
                    <Typography color="text.secondary" variant="body2">
                      I am married to a non-US citizen.
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      alignItems: "center",
                      display: "flex",
                    }}
                  >
                    <Checkbox
                      checked={checkedSituations.charity}
                      name="charity"
                      onChange={handleSituationChange("charity")}
                    />
                    <Typography color="text.secondary" variant="body2">
                      I want to make a large, tax-advantaged donation.
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      alignItems: "center",
                      display: "flex",
                    }}
                  >
                    <Checkbox
                      checked={checkedSituations.international}
                      name="international"
                      onChange={handleSituationChange("international")}
                    />
                    <Typography color="text.secondary" variant="body2">
                      I own significant property in another country.
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      alignItems: "center",
                      display: "flex",
                    }}
                  >
                    <Checkbox
                      checked={checkedSituations.creditors}
                      name="creditors"
                      onChange={handleSituationChange("creditors")}
                    />
                    <Typography color="text.secondary" variant="body2">
                      I am being sued, or am concerned about creditors.
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      alignItems: "center",
                      display: "flex",
                    }}
                  >
                    <Checkbox
                      checked={checkedSituations.fight}
                      name="fight"
                      onChange={handleSituationChange("fight")}
                    />
                    <Typography color="text.secondary" variant="body2">
                      I believe my family will fight over my estate.
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      alignItems: "center",
                      display: "flex",
                    }}
                  >
                    <Checkbox
                      checked={checkedSituations.none}
                      name="none"
                      onChange={handleSituationChange("none")}
                    />
                    <Typography color="text.secondary" variant="body2">
                      None of these apply to me.
                    </Typography>
                  </Box>
                  <Stack spacing={1} sx={{ mb: "-40px !important", mt: 3 }}>
                    <LoadingButton
                      disabled={
                        headOfHouseholDataSaving ||
                        Object.values(checkedSituations).every((v) => !v)
                      }
                      fullWidth
                      endIcon={<ArrowForwardIcon />}
                      size="large"
                      sx={{ mt: 4 }}
                      type="button"
                      variant="contained"
                      loading={headOfHouseholDataSaving}
                      onClick={async () => {
                        try {
                          if (!headOfHousehold?.ud_id) return;
                          // Convert checkedSituations object to array of selected keys
                          const selectedSituations = Object.keys(
                            checkedSituations
                          ).filter(
                            (key) =>
                              checkedSituations[
                              key as keyof typeof checkedSituations
                              ]
                          );
                          await onboardingHandleEditHoHUserData(
                            headOfHousehold.ud_id,
                            {
                              special_situations: selectedSituations,
                            }
                          );
                          setOnboardingStep("loader");
                        } catch (err) {
                          console.error(err);
                        }
                      }}
                    >
                      {`Save and continue`}
                    </LoadingButton>
                    <Button
                      variant="text"
                      color="inherit"
                      onClick={() => setOnboardingStep("goals")}
                    >
                      Back
                    </Button>
                  </Stack>
                </Stack>
              )}
              {onboardingStep === "loader" && <EstatePlanLoader />}
              {onboardingStep === "personal_plan" && (
                <Stack spacing={3}>
                  <Stack spacing={2}>
                    {[...personalPlanOptionsAllowed]
                      .sort((a, b) => {
                        if (a.value === recommendedPlan) return -1;
                        if (b.value === recommendedPlan) return 1;
                        return 0;
                      })
                      .map((option) => (
                        <>
                          {!(
                            maritalStatus === "single" &&
                            option.value === "joint"
                          ) && (
                              <Card
                                sx={{
                                  alignItems: "center",
                                  cursor: "pointer",
                                  display: "flex",
                                  p: 1.2,
                                  ...(personalPlan === option.value && {
                                    backgroundColor: "primary.alpha12",
                                    boxShadow: (theme) =>
                                      `${theme.palette.primary.main} 0 0 0 1px`,
                                  }),
                                }}
                                onClick={(e) => {
                                  setPersonalPlan(option.value);
                                }}
                                variant="outlined"
                              >
                                <Stack
                                  direction="row"
                                  spacing={2}
                                  alignItems="center"
                                  sx={{ width: "100%" }}
                                >
                                  <Radio
                                    checked={personalPlan === option.value}
                                    color="primary"
                                  />
                                  <Stack spacing={1} sx={{ width: "100%" }}>
                                    <Stack
                                      direction="row"
                                      justifyContent={"space-between"}
                                      alignItems={"center"}
                                      sx={{ width: "100%" }}
                                    >
                                      {option.value === "attorney" ||
                                        option.value === "skip" ? (
                                        <Typography variant="subtitle2">
                                          {option.title}
                                        </Typography>
                                      ) : (
                                        <Typography variant="subtitle2">
                                          {option.title}
                                          {maritalStatus !== "single" &&
                                            option.value !== "joint" &&
                                            `s (`}
                                          {maritalStatus !== "single" &&
                                            option.value === "joint" &&
                                            ` (`}
                                          {maritalStatus !== "single" &&
                                            option.plural}
                                          {maritalStatus !== "single" && ")"}
                                        </Typography>
                                      )}
                                      {recommendedPlan === option.value ? (
                                        <SeverityPill
                                          color="success"
                                          sx={{ textTransform: "none" }}
                                        >
                                          Best Fit
                                        </SeverityPill>
                                      ) : (
                                        <>
                                          {option.value !== "attorney" &&
                                            option.value !== "skip" &&
                                            personalPlan !== option.value &&
                                            selectedHousehold?.household_role
                                              ?.professional_pricing_option !==
                                            "ON_PRO_TAB" && selectedHousehold?.professional_group?.allow_client_charges && (
                                              <Typography variant="body2">
                                                {formatUSD(
                                                  optionCost(option.value)
                                                )}
                                              </Typography>
                                            )}
                                        </>
                                      )}
                                    </Stack>
                                    {(personalPlan === option.value ||
                                      recommendedPlan === option.value) && (
                                        <>
                                          {recommendedPlan === option.value ? (
                                            <Typography
                                              color="text.secondary"
                                              variant="body2"
                                            >
                                              {recommendedPlanExplainer}
                                            </Typography>
                                          ) : (
                                            <Typography
                                              color="text.secondary"
                                              variant="body2"
                                            >
                                              {option.description}
                                            </Typography>
                                          )}
                                        </>
                                      )}
                                    {option.value !== "attorney" &&
                                      option.value !== "skip" &&
                                      selectedHousehold?.household_role
                                        ?.professional_pricing_option !==
                                      "ON_PRO_TAB" && selectedHousehold?.professional_group?.allow_client_charges && (
                                        <>
                                          {personalPlan === option.value && (
                                            <Typography variant="body2">
                                              Total cost:{" "}
                                              {formatUSD(
                                                optionCost(option.value)
                                              )}
                                            </Typography>
                                          )}
                                        </>
                                      )}
                                  </Stack>
                                </Stack>
                              </Card>
                            )}
                        </>
                      ))}
                  </Stack>
                  <Stack spacing={1} sx={{ mb: "-40px !important" }}>
                    <LoadingButton
                      disabled={headOfHouseholDataSaving || !personalPlan}
                      fullWidth
                      endIcon={<ArrowForwardIcon />}
                      size="large"
                      sx={{ mt: 4 }}
                      type="button"
                      variant="contained"
                      loading={headOfHouseholDataSaving}
                      onClick={async () => {
                        try {
                          if (!headOfHousehold?.ud_id) return;
                          await onboardingHandleEditHoHUserData(
                            headOfHousehold.ud_id,
                            {
                              estate_plan_foundation: personalPlan,
                              show_household_onboarding_requirement: false,
                            }
                          );

                          // Navigate back to the original path if returnPath is provided, otherwise go to dashboard
                          // Navigate back to the original path if returnPath is provided, otherwise go to dashboard
                          let navigationPath =
                            returnPath || paths.dashboard.index;
                          // Add personal plan as another parameter on returnPath if applicable
                          if (
                            personalPlan &&
                            ["joint", "will", "trust"].includes(personalPlan)
                          ) {
                            const separator = navigationPath.includes("?")
                              ? "&"
                              : "?";
                            navigationPath += `${separator}personal_plan=${personalPlan}`;
                          }
                          router.push(navigationPath);
                        } catch (err) {
                          console.error(err);
                        }
                      }}
                    >
                      {`Save and continue`}
                    </LoadingButton>
                    <Button
                      variant="text"
                      color="inherit"
                      onClick={() => setOnboardingStep("situations")}
                    >
                      Back
                    </Button>
                  </Stack>
                </Stack>
              )}
            </CardContent>
          </Card>
          <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
            <Button onClick={handleSkipOnboarding} variant="text">
              {headOfHousehold?.estate_plan_foundation
                ? `Back to home page`
                : `Skip creating a personal plan`}
            </Button>
          </Box>
        </Stack>
      </div>
    </>
  );
};

export default Onboarding;
